// src/modules/shared/helpers/fetch-with-auth.helper.tsx

import { AuthenticationEntity } from "@modules/auth/entities/auth.entity";
import { authSyncService } from "@modules/auth/services/auth-sync.service";
import { AuthenticationService } from "@modules/auth/services/auth.service";

let refreshingPromise: Promise<AuthenticationEntity | null> | null = null;

const buildErrorMessage = (errData: unknown, status: number) => {
  if (!errData) return `Error ${status}`;
  if (typeof errData === "string") return errData;
  if (typeof errData !== "object") return `Error ${status}`;

  const record = errData as Record<string, unknown>;
  const message =
    record.message ??
    record.error ??
    record.errors ??
    record.title ??
    record.subtitle ??
    record.details;

  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.filter(Boolean).join(", ");

  try {
    return JSON.stringify(message ?? errData);
  } catch {
    return `Error ${status}`;
  }
};

const saveTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem("access_token", accessToken);
  sessionStorage.setItem("access_token", accessToken);
  localStorage.setItem("refresh_token", refreshToken);
  sessionStorage.setItem("refresh_token", refreshToken);
};

export async function fetchWithAuth(
  url: string,
  options: RequestInit,
  auth: AuthenticationEntity | null,
  setAuth: (auth: AuthenticationEntity | null) => void,
  logout: () => void,
  onStartLoading?: () => void,
  onStopLoading?: () => void,
) {
  onStartLoading?.();

  const doFetch = async (token?: string) => {
    return await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : "",
      },
    });
  };

  try {
    let response = await doFetch(auth?.getAccessToken());

    if (response.status === 401 && auth?.getRefreshToken()) {
      if (!refreshingPromise) {
        refreshingPromise = new AuthenticationService()
          .refresh(auth.getRefreshToken())
          .then((res) => {
            refreshingPromise = null;
            if (res instanceof AuthenticationEntity) {
              saveTokens(res.getAccessToken(), res.getRefreshToken());
              setAuth(res);
              authSyncService.broadcastTokenRefresh(
                res.getAccessToken(),
                res.getRefreshToken(),
              );
              return res;
            } else {
              // Refresh thất bại (ErrorEntity) => logout
              logout();
              return null;
            }
          })
          .catch((err) => {
            refreshingPromise = null;
            console.error("Refresh token error:", err);
            logout();
            return null;
          });
      }

      const newAuth = await refreshingPromise;
      if (!newAuth) {
        throw new Error("Unauthorized - session expired");
      }

      response = await doFetch(newAuth.getAccessToken());
    }

    if (response.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }

    // Nếu response không ok, parse lỗi và throw
    if (!response.ok) {
      let errData = {};
      try {
        errData = await response.json();
      } catch {}
      throw new Error(buildErrorMessage(errData, response.status));
    }

    const text = await response.text();
    if (!text.trim()) {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes("Unauthorized")) {
      logout();
    }
    throw error;
  } finally {
    onStopLoading?.();
  }
}
